import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useForceCancelAction, type ForceCancelVariables } from '../../../api/admin-orders';
import { publishToast } from '../../../components/feedback';

/**
 * Encapsulates the force-cancel orchestration for the order detail drawer
 * (AUT-123): fires the destructive POST, a success / error toast, and
 * invalidates the `['admin','orders']` cache so the list + drawer refetch.
 * Extracted from the drawer so the body stays under the line cap. The
 * action is role + permission + audit gated server-side (AUT-138 removed
 * per-action TOTP step-up).
 *
 * The force-cancel response envelope (`{dto, previousStatus}`) is NOT used
 * to patch the row — `invalidate()` refetches the admin projections
 * instead (architect guardrail #2: `dto` is the user-facing OrderDto).
 */
export interface UseForceCancelResult {
  /** True while the mutation is in flight. */
  busy: boolean;
  /** Run a force-cancel; resolves true on success, false on error. */
  run: (variables: ForceCancelVariables) => Promise<boolean>;
}

export const useForceCancel = (): UseForceCancelResult => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const { action, invalidate } = useForceCancelAction();

  const run = async (variables: ForceCancelVariables): Promise<boolean> => {
    setBusy(true);
    try {
      await action(variables);
      await invalidate();
      publishToast({
        severity: 'success',
        durationMs: 4000,
        message: t('orders.forceCancelDialog.successToast'),
      });
      return true;
    } catch {
      publishToast({
        severity: 'error',
        durationMs: 6000,
        message: t('orders.forceCancelDialog.errorToast'),
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
};
